// ═══════════════════════════════════════════════════════════════
// NotificationSettings.jsx — Push toggle + per-category email
// opt-outs. Shared by the full app profile panel (DashboardView)
// and the free-tier profile panel (FreemiumProfile) so the two
// never drift apart. The category list itself lives in shared.jsx
// alongside the keys the Cloud Functions read.
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { Bell, BellOff, Mail } from 'lucide-react';
import { EMAIL_NOTIF_CATEGORIES } from '../shared.jsx';

/**
 * @param {object}   user                  App user record (carries the emailNotif* flags)
 * @param {boolean}  notificationsEnabled  Browser push permission state
 * @param {Function} onToggleNotifications Handler for the push toggle
 * @param {Function} onUpdateUser          Persists a changed user record
 * @param {Function} onToast               Optional feedback callback
 */
function NotificationSettings({ user, notificationsEnabled, onToggleNotifications, onUpdateUser, onToast }) {
  const announce = (message) => { if (onToast) onToast(message); };

  return (
    <>
      {/* Push Notifications */}
      <div className="p-3 bg-stone-50 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {notificationsEnabled ? (
              <Bell className="w-4 h-4 text-amber-700" />
            ) : (
              <BellOff className="w-4 h-4 text-stone-500" />
            )}
            <span className="text-sm font-medium text-stone-700">Push Notifications</span>
          </div>
          <button
            onClick={onToggleNotifications}
            aria-pressed={!!notificationsEnabled}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              notificationsEnabled
                ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                : 'bg-stone-200 text-stone-600 hover:bg-stone-300'
            }`}
          >
            {notificationsEnabled ? 'Enabled' : 'Enable'}
          </button>
        </div>
        {!notificationsEnabled && 'Notification' in window && Notification.permission === 'denied' && (
          <p className="text-xs text-stone-500 mt-2">
            Notifications are blocked. Click the lock icon in your browser's address bar to allow them.
          </p>
        )}
      </div>

      {/* Email Notifications */}
      <div className="p-3 bg-stone-50 rounded-lg">
        <div className="flex items-center gap-2 mb-1">
          <Mail className="w-4 h-4 text-amber-700" />
          <span className="text-sm font-medium text-stone-700">Email Notifications</span>
        </div>
        <p className="text-xs text-stone-500 mb-3">These emails are sent only when a push notification can't be delivered. Choose which categories you'd like to receive.</p>
        {EMAIL_NOTIF_CATEGORIES.map(({ key, label }) => {
          const enabled = user[key] !== false;
          return (
            <div key={key} className="flex items-center justify-between py-1.5">
              <span className="text-xs text-stone-700" id={`notif-label-${key}`}>{label}</span>
              <button
                aria-pressed={enabled}
                aria-labelledby={`notif-label-${key}`}
                onClick={async () => {
                  if (onUpdateUser) {
                    await onUpdateUser({ ...user, [key]: !enabled });
                    announce(enabled ? 'Emails disabled for this category' : 'Emails enabled for this category');
                  }
                }}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  enabled
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-stone-200 text-stone-600 hover:bg-stone-300'
                }`}
              >
                {enabled ? 'On' : 'Off'}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default NotificationSettings;
